import { FlatQuotationLine, SmallLotPricingTier } from '@ravago/shared/page-data/models/elements';

import Decimal from 'decimal.js';

import { QuantityRange, getNearestValidQuantities } from './quantity-steps.utils';
import { getLastTierQuantityTo, getTierForQuantity, sortTiers } from './small-lot-tier.utils';

export interface CombinedQuoteQuantity {
   quantity: number;
   quantityTo?: number;
   line: FlatQuotationLine;
}

export type CombinedSuggestion =
   | { kind: 'quote'; quantity: number; line: FlatQuotationLine }
   | { kind: 'small-lot'; quantity: number; tier: SmallLotPricingTier };

export type CombinedResolution =
   | { kind: 'quote'; quantity: number; line: FlatQuotationLine; matchingLines: FlatQuotationLine[] }
   | { kind: 'small-lot'; quantity: number; tier: SmallLotPricingTier }
   | { kind: 'suggestion'; lower?: CombinedSuggestion; higher?: CombinedSuggestion; suggestions: CombinedSuggestion[] };

export interface CombinedPricingParams {
   quoteLines: FlatQuotationLine[];
   tiers: SmallLotPricingTier[];
   netWeight: number | undefined;
   preferredQuoteLineId?: string;
}

export interface QuotePricingParams {
   quoteLines: FlatQuotationLine[];
   preferredQuoteLineId?: string;
}

interface QuoteRangeCandidate {
   quantity: number;
   quantityTo?: number;
   line: FlatQuotationLine;
}

interface QuoteSuggestionPoint {
   quantity: number;
   line: FlatQuotationLine;
}

const getPackagingKey = (line: FlatQuotationLine): string => `${line.linePackagingGroupId ?? 'NO_GROUP'}::${line.linePackaging}`;

export const compareQuoteLines = (a: FlatQuotationLine, b: FlatQuotationLine): number => {
   const priceDiff = a.lineUnitPrice.price.amount - b.lineUnitPrice.price.amount;
   if (priceDiff !== 0) return priceDiff;

   const packagingDiff = a.linePackaging.localeCompare(b.linePackaging);
   if (packagingDiff !== 0) return packagingDiff;

   const quoteNameDiff = a.quotationName.localeCompare(b.quotationName);
   if (quoteNameDiff !== 0) return quoteNameDiff;

   return a.lineId.localeCompare(b.lineId);
};

const getPackagingIncrement = (line: FlatQuotationLine): number | undefined => {
   if (line.lineNetWeightAmount !== undefined && line.lineNetWeightAmount > 0) return line.lineNetWeightAmount;

   return undefined;
};

const getQuoteIncrementForAmount = (line: FlatQuotationLine, amount: number, _baseIncrement: number | undefined): number | undefined => {
   const palletSize = line.linePalletSizeAmount;
   const packagingIncrement = getPackagingIncrement(line);

   if (palletSize !== undefined && palletSize > 0 && amount >= palletSize) return palletSize;
   if (packagingIncrement !== undefined && packagingIncrement > 0) return packagingIncrement;

   return undefined;
};

const toQuantityRange = ({ quantity, quantityTo, line }: QuoteRangeCandidate): QuantityRange => ({
   from: quantity,
   to: quantityTo,
   palletSize: line.linePalletSizeAmount,
   increment: getPackagingIncrement(line)
});

const isQuoteQuantityMatch = (candidate: QuoteRangeCandidate, q: number): boolean => getNearestValidQuantities(toQuantityRange(candidate), q).isValid;

/** Per range, the orderable quantities right below and above `q`. */
const getQuoteSuggestionPoints = (candidates: QuoteRangeCandidate[], q: number): QuoteSuggestionPoint[] =>
   candidates.flatMap((candidate) => {
      const { lower, higher } = getNearestValidQuantities(toQuantityRange(candidate), q);
      return [lower, higher]
         .filter((quantity): quantity is number => quantity !== undefined)
         .map((quantity) => ({ quantity, line: candidate.line }));
   });

const toStableQuoteCandidate = (line: FlatQuotationLine, nextDistinctQuantity: number | undefined, baseIncrement: number | undefined): QuoteRangeCandidate => {
   const explicitTo = line.lineQuantityToAmount;
   const fallbackIncrement = getQuoteIncrementForAmount(line, line.lineQuantityAmount, baseIncrement);
   const fallbackTo =
      explicitTo === undefined && nextDistinctQuantity !== undefined && fallbackIncrement !== undefined && fallbackIncrement > 0
         ? new Decimal(nextDistinctQuantity).minus(fallbackIncrement).toNumber()
         : undefined;

   const resolvedTo = explicitTo ?? fallbackTo;

   return {
      quantity: line.lineQuantityAmount,
      quantityTo: resolvedTo !== undefined && resolvedTo > line.lineQuantityAmount ? resolvedTo : undefined,
      line
   };
};

const getQuoteRangeCandidates = (quoteLines: FlatQuotationLine[], increment: number | undefined): QuoteRangeCandidate[] => {
   const byPackaging = new Map<string, FlatQuotationLine[]>();

   for (const line of quoteLines) {
      const key = getPackagingKey(line);
      const list = byPackaging.get(key) ?? [];
      list.push(line);
      byPackaging.set(key, list);
   }

   const ranges: QuoteRangeCandidate[] = [];

   for (const lines of byPackaging.values()) {
      const sorted = [...lines].sort((a, b) => a.lineQuantityAmount - b.lineQuantityAmount || compareQuoteLines(a, b));

      const distinctQuantities = Array.from(new Set(sorted.map((line) => line.lineQuantityAmount))).sort((a, b) => a - b);
      const nextDistinctQuantityByAmount = new Map<number, number | undefined>();
      for (let i = 0; i < distinctQuantities.length; i++) {
         nextDistinctQuantityByAmount.set(distinctQuantities[i], distinctQuantities[i + 1]);
      }

      for (const line of sorted) {
         ranges.push(toStableQuoteCandidate(line, nextDistinctQuantityByAmount.get(line.lineQuantityAmount), increment));
      }
   }

   return ranges;
};

/**
 * Collapses quote lines into display rows grouped by packaging + quantity, picking the
 * deterministic default line per row and preserving an optional display range upper bound.
 */
export const getDistinctQuoteQuantities = (quoteLines: FlatQuotationLine[], increment: number | undefined = undefined): CombinedQuoteQuantity[] => {
   const byPackagingAndQuantity = new Map<string, QuoteRangeCandidate>();

   for (const candidate of getQuoteRangeCandidates(quoteLines, increment)) {
      const key = `${getPackagingKey(candidate.line)}::${candidate.quantity}`;
      const existing = byPackagingAndQuantity.get(key);

      if (!existing || compareQuoteLines(candidate.line, existing.line) < 0) {
         byPackagingAndQuantity.set(key, candidate);
      }
   }

   return Array.from(byPackagingAndQuantity.values())
      .map(({ quantity, quantityTo, line }) => ({ quantity, quantityTo, line }))
      .sort((a, b) => a.quantity - b.quantity || a.line.linePackaging.localeCompare(b.line.linePackaging) || compareQuoteLines(a.line, b.line));
};

export const getLowestQuoteQuantity = (quoteLines: FlatQuotationLine[]): number | null => {
   if (quoteLines.length === 0) return null;
   return Math.min(...quoteLines.map((line) => line.lineQuantityAmount));
};

export const getHighestQuoteQuantity = (quoteLines: FlatQuotationLine[]): number | null => {
   if (quoteLines.length === 0) return null;
   return Math.max(...quoteLines.map((line) => line.lineQuantityAmount));
};

/** Smallest small-lot accepted quantity (the packaging increment), or null when no small-lot. */
export const getLowestSmallLotQuantity = (netWeight: number | undefined): number | null => netWeight ?? null;

/** Closest quote option on one side of `q` for each packaging, nearest first. */
const findQuoteSuggestionsPerPackaging = (points: QuoteSuggestionPoint[], q: number, side: 'lower' | 'higher'): CombinedSuggestion[] => {
   const sign = side === 'lower' ? -1 : 1;
   const compareByCloseness = (a: QuoteSuggestionPoint, b: QuoteSuggestionPoint): number =>
      sign * (a.quantity - b.quantity) || compareQuoteLines(a.line, b.line);

   const bestByPackaging = new Map<string, QuoteSuggestionPoint>();
   for (const point of points) {
      if (sign * (point.quantity - q) <= 0) continue;

      const key = getPackagingKey(point.line);
      const best = bestByPackaging.get(key);
      if (!best || compareByCloseness(point, best) < 0) bestByPackaging.set(key, point);
   }

   return Array.from(bestByPackaging.values())
      .sort(compareByCloseness)
      .map(({ quantity, line }) => ({ kind: 'quote', quantity, line }));
};

/** Greatest orderable small-lot quantity: the largest multiple of the increment strictly below the lowest quote (unbounded when there are no quotes). */
const getSmallLotMax = (hasSmallLot: boolean, lowestQuote: number, increment: number): number => {
   if (!hasSmallLot) return 0;
   if (!Number.isFinite(lowestQuote)) return Number.POSITIVE_INFINITY;
   return Math.floor((lowestQuote - 1) / increment) * increment;
};

/**
 * Resolves a typed quantity against the combined accepted set.
 *
 * Rules (quotes present):
 *   - exact quote quantity → valid quote.
 *   - positive multiple of `netWeight` strictly below the lowest quote → valid small-lot
 *     (the price caps at the best/last tier for quantities above the last tier's upper bound).
 *   - anything else → suggestion (closest lower + closest higher accepted quantity).
 *   - at/above the lowest quote, only exact quotes are accepted; a non-quote value there is suggested.
 *   - the highest quote is the hard maximum: above it, only the lower (highest-quote) suggestion.
 */
export const resolveCombinedQuantity = (q: number, params: CombinedPricingParams): CombinedResolution => {
   const { quoteLines, tiers, netWeight, preferredQuoteLineId } = params;

   const sortedTiers = sortTiers(tiers);
   const hasSmallLot = sortedTiers.length > 0 && netWeight !== undefined && netWeight > 0;
   const lastTierQuantityTo = getLastTierQuantityTo(sortedTiers);
   const quoteIncrement = netWeight !== undefined && netWeight > 0 ? netWeight : undefined;
   const quoteQuantities = getDistinctQuoteQuantities(quoteLines, quoteIncrement);
   const quoteRangeCandidates = getQuoteRangeCandidates(quoteLines, quoteIncrement);
   const quoteSuggestionPoints = getQuoteSuggestionPoints(quoteRangeCandidates, q);
   const lowestQuote = quoteQuantities[0]?.quantity ?? Number.POSITIVE_INFINITY;
   const increment = netWeight ?? 0;

   // Largest orderable small-lot quantity: the greatest multiple of the increment strictly below
   // the lowest quote (small-lot can be ordered right up to — but not including — the lowest quote).
   const smallLotMax = getSmallLotMax(hasSmallLot, lowestQuote, increment);

   const matchingQuoteCandidates = quoteRangeCandidates
      .filter((candidate) => isQuoteQuantityMatch(candidate, q))
      .sort((a, b) => compareQuoteLines(a.line, b.line));

   if (matchingQuoteCandidates.length > 0) {
      const preferredCandidate =
         preferredQuoteLineId === undefined
            ? undefined
            : matchingQuoteCandidates.find((candidate) => candidate.line.lineId === preferredQuoteLineId);
      const selected = preferredCandidate ?? matchingQuoteCandidates[0];

      return {
         kind: 'quote',
         quantity: q,
         line: selected.line,
         matchingLines: matchingQuoteCandidates.map((candidate) => candidate.line)
      };
   }

   // Valid small-lot: positive multiple of the increment, below the lowest quote.
   if (hasSmallLot && q > 0 && q % increment === 0 && q <= smallLotMax) {
      const tier = getTierForQuantity(q, sortedTiers, lastTierQuantityTo);
      if (tier) return { kind: 'small-lot', quantity: q, tier };
   }

   // Otherwise: build the closest lower / higher suggestions across the combined set.
   const smallLotLowerQty = (() => {
      if (!hasSmallLot || q <= 0) return null;
      if (q > smallLotMax) return smallLotMax >= increment ? smallLotMax : null; // clamp to the top of small-lot
      const floored = Math.floor(q / increment) * increment;
      return floored >= increment ? floored : null;
   })();

   const smallLotHigherQty = (() => {
      if (!hasSmallLot) return null;
      // When the input exceeds the defined small-lot range (last tier upper bound) and quotes
      // are available, nudge towards the quote instead of the next small-lot increment.
      if (q > lastTierQuantityTo && quoteQuantities.length > 0) return null;
      const ceiled = q <= 0 ? increment : Math.ceil(q / increment) * increment;
      return ceiled >= increment && ceiled <= smallLotMax ? ceiled : null;
   })();

   const quoteLowerSuggestions = findQuoteSuggestionsPerPackaging(quoteSuggestionPoints, q, 'lower');
   const quoteHigherSuggestions = findQuoteSuggestionsPerPackaging(quoteSuggestionPoints, q, 'higher');
   const quoteLowerSuggestion = quoteLowerSuggestions[0] ?? null;
   const quoteHigherSuggestion = quoteHigherSuggestions[0] ?? null;

   const toSmallLotSuggestion = (quantity: number | null): CombinedSuggestion | null => {
      if (quantity === null) return null;
      const tier = getTierForQuantity(quantity, sortedTiers, lastTierQuantityTo);
      return tier ? { kind: 'small-lot', quantity, tier } : null;
   };

   const smallLotLower = toSmallLotSuggestion(smallLotLowerQty);
   const smallLotHigher = toSmallLotSuggestion(smallLotHigherQty);
   // Closest lower: the greater of the two candidates.
   const lower = [smallLotLower, quoteLowerSuggestion]
      .filter((s): s is CombinedSuggestion => s !== null)
      .sort((a, b) => b.quantity - a.quantity)[0];

   // Closest higher: the smaller of the two candidates.
   const higher = [smallLotHigher, quoteHigherSuggestion]
      .filter((s): s is CombinedSuggestion => s !== null)
      .sort((a, b) => a.quantity - b.quantity)[0];

   // Quote suggestions on a side cover every packaging, so overlapping packagings each get an option.
   const sideSuggestions = (closest: CombinedSuggestion | undefined, quoteSuggestions: CombinedSuggestion[]): CombinedSuggestion[] => {
      if (!closest) return [];
      return closest.kind === 'quote' ? quoteSuggestions : [closest];
   };

   return {
      kind: 'suggestion',
      lower,
      higher,
      suggestions: [...sideSuggestions(lower, quoteLowerSuggestions), ...sideSuggestions(higher, quoteHigherSuggestions)]
   };
};

/**
 * Resolves a typed quantity against quotes only: an exact quote quantity is valid, anything else
 * yields the closest lower/higher quoted suggestions.
 */
export const resolveQuoteQuantity = (q: number, { quoteLines, preferredQuoteLineId }: QuotePricingParams): CombinedResolution =>
   resolveCombinedQuantity(q, { quoteLines, tiers: [], netWeight: undefined, preferredQuoteLineId });
